/**
 * Fatura sem injeção de energia no ciclo (UC não recebeu compensação SCEE) não
 * deveria virar boleto da Vigor — só existe a fatura da distribuidora. O sistema
 * já sabia disso num único fluxo (FaturaValidationModal.handleSkipCalculation);
 * este módulo centraliza a regra para os demais pontos que escrevem status_fatura
 * (FaturaCalculationModal.handleConfirm, PendentesCalculoModal.aplicarSkipNaFatura)
 * pararem de criar boleto "fantasma" — valor positivo herdado de uma duplicata da
 * mesma UC ou de um cálculo anterior, sem nenhum boleto real emitido.
 */

export function temInjecao(f: {
  energia_injetada_gd1_kwh?: number | string | null;
  energia_injetada_gd2_kwh?: number | string | null;
}): boolean {
  const gd1 = Number(f.energia_injetada_gd1_kwh) || 0;
  const gd2 = Number(f.energia_injetada_gd2_kwh) || 0;
  return gd1 + gd2 > 0;
}

/**
 * Campos "crus" ligados matematicamente ao evento de injeção do ciclo — na
 * fórmula de calcularValoresBoleto, cada um só entra multiplicado ou originado
 * pela injeção. Sem injeção, o valor correto é sempre zero: seguro zerar sem
 * pedir confirmação humana.
 */
export const CAMPOS_ORIGEM_LIGADOS_A_INJECAO = {
  valor_parc_injet_s_desc: 0,
  valor_restituicao_imposto: 0,
  energia_nao_isenta: 0,
} as const;

/**
 * Payload de "zerar boleto por falta de injeção" — fonte única para os pontos
 * que gravam faturas sem cobrança da Vigor. Deliberadamente NÃO inclui
 * valor_juros_boleto / outros_multas_boleto / valor_parcelamentos_boleto: são
 * dívida real (juros de atraso, multa, parcela de acordo) que não desaparece só
 * porque a usina não gerou este mês — nunca zerar esses três automaticamente.
 */
export const CAMPOS_ZERADOS_SEM_INJECAO = {
  valor_consumo_nao_comp: 0,
  valor_adc_bandeira: 0,
  ...CAMPOS_ORIGEM_LIGADOS_A_INJECAO,
  valor_boleto_vigor: 0,
  valor_consumo_scee_boleto: 0,
  valor_adc_bandeira_boleto: 0,
  valor_parc_injet_s_desc_boleto: 0,
  valor_restituicao_imposto_boleto: 0,
  valor_energia_nao_isenta_boleto: 0,
  custo_s_vigor: 0,
  economia: 0,
  total_com_vigor: 0,
  // Não há boleto emitido nesse caminho — limpa o status cru para não deixar
  // rastro de um cálculo anterior confundir a badge de status.
  status_boleto_vigor: null,
  // Mesma razão, para a data: zerar o valor e limpar o status não bastava
  // enquanto `vencimento_boleto` continuava apontando para o boleto que não
  // existe mais. Toda tela que classifica vencido por data ganhava uma fatura
  // fantasma — sem badge, sem boleto e sem opção de quitar (chamado da UC
  // 65083301222, FABRIK LTDA, AGO/2026). A data do boleto pertence ao boleto;
  // sem boleto ela não tem dono. `data_vencimento` (o vencimento da fatura da
  // distribuidora) é outro campo e continua intacto.
  vencimento_boleto: null,
} as const;

/**
 * O payload acima e, quando a fatura NÃO tem boleto vivo no banco, também o valor
 * registrado do boleto (`valor_emissao_boleto_unico`). `valorDevido`
 * (statusBoleto.ts) olha esse campo ANTES do valor Vigor: sobrando o valor do
 * boleto cancelado, a fatura sem cobrança seguia "devendo" e virava "Vencido"
 * quando a data da distribuidora passava (#6246, R R FREIRE LANCHONETE, AGO/2026:
 * R$ 783,97 fantasma). Com boleto vivo (`num_cobranca_boleto`), o campo é o valor
 * de uma cobrança que existe — boleto único de parceira só com a parte da
 * Equatorial, por exemplo — e não pode sumir.
 */
export function camposZeradosSemInjecao(
  f: { num_cobranca_boleto?: string | null } | null | undefined,
): typeof CAMPOS_ZERADOS_SEM_INJECAO & { valor_emissao_boleto_unico?: null } {
  return f?.num_cobranca_boleto
    ? { ...CAMPOS_ZERADOS_SEM_INJECAO }
    : { ...CAMPOS_ZERADOS_SEM_INJECAO, valor_emissao_boleto_unico: null };
}
